import { createPlotFeature, } from "@plotlibre/core";
import { MultiPointDrawSession, SelectionController, TwoPointDrawSession, } from "@plotlibre/interaction";
import { MapLibrePlotRenderer } from "./renderer.js";
export class MapLibrePlotInteraction {
    #map;
    #registry;
    #store;
    #renderer;
    #callbacks;
    #idFactory;
    #selection;
    #ownsSelection;
    #unsubscribeStore;
    #unsubscribeSelection;
    #session;
    #draftFeature;
    #drawRejection;
    #drag;
    #doubleClickZoomWasEnabled;
    #doubleClickZoomRestoreTimer;
    #suppressNextClick = false;
    #clickSuppressionTimer;
    #onClick = (event) => {
        const mouseEvent = asMouseEvent(event);
        if (!mouseEvent)
            return;
        this.#focusCanvas();
        if (this.#suppressNextClick) {
            this.#clearClickSuppression();
            return;
        }
        if (this.#session) {
            mouseEvent.originalEvent?.preventDefault?.();
            this.#applyDrawSnapshot(this.#session.click(toPosition(mouseEvent)));
            return;
        }
        const plotId = this.#queryPlotId(mouseEvent);
        this.#selection.applyIntent(plotId, readSelectionIntent(mouseEvent));
        this.#syncSelection();
    };
    #onDoubleClick = (event) => {
        if (!this.#session)
            return;
        const mouseEvent = asMouseEvent(event);
        if (!mouseEvent)
            return;
        mouseEvent.originalEvent?.preventDefault?.();
        mouseEvent.originalEvent?.stopPropagation?.();
        this.#focusCanvas();
        this.#applyDrawSnapshot(this.#session.doubleClick(toPosition(mouseEvent)), { deferDoubleClickZoomRestore: true });
    };
    #onMouseMove = (event) => {
        const mouseEvent = asMouseEvent(event);
        if (!mouseEvent)
            return;
        if (this.#drag) {
            this.#updateDrag(toPosition(mouseEvent));
            return;
        }
        if (this.#session) {
            this.#applyDrawSnapshot(this.#session.pointerMove(toPosition(mouseEvent)));
            return;
        }
        if (this.#selection.primaryId && this.#queryHandle(mouseEvent)) {
            this.#setCursor("grab");
        }
        else {
            this.#setCursor("");
        }
    };
    #onMouseDown = (event) => {
        if (this.#session)
            return;
        const mouseEvent = asMouseEvent(event);
        if (!mouseEvent)
            return;
        const handle = this.#queryHandle(mouseEvent);
        if (!handle)
            return;
        const properties = handle.properties;
        const plotId = readString(properties?.plotId);
        const handleIndex = readInteger(properties?.handleIndex);
        if (plotId === undefined || handleIndex === undefined)
            return;
        if (plotId !== this.#selection.primaryId)
            return;
        const original = this.#store.get(plotId);
        if (!original.controlPoints[handleIndex])
            return;
        const dragPanWasEnabled = this.#map.dragPan?.isEnabled?.() ?? true;
        this.#map.dragPan?.disable();
        this.#drag = {
            original,
            handleIndex,
            dragPanWasEnabled,
            preview: original,
            moved: false,
        };
        this.#draftFeature = original;
        this.#renderer.renderDraft(original, this.#registry);
        mouseEvent.originalEvent?.preventDefault?.();
        mouseEvent.originalEvent?.stopPropagation?.();
        this.#focusCanvas();
        this.#setCursor("grabbing");
    };
    #onMouseUp = () => {
        if (!this.#drag)
            return;
        const drag = this.#drag;
        this.#drag = undefined;
        try {
            if (drag.moved) {
                this.#callbacks.replace(drag.preview);
            }
        }
        finally {
            this.#draftFeature = undefined;
            this.#renderer.clearDraft();
            this.#restoreDragPan(drag);
            this.#syncSelection();
            this.#armClickSuppression();
            this.#setCursor(this.#selection.primaryId ? "grab" : "");
        }
    };
    #onStyleLoad = () => {
        this.#renderer.initialize();
        this.#renderer.render(this.#store.list(), this.#registry);
        if (this.#draftFeature) {
            this.#renderer.renderDraft(this.#draftFeature, this.#registry);
        }
        this.#syncSelection();
    };
    #onKeyDown = (event) => {
        if (this.#drag && event.key === "Escape") {
            event.preventDefault?.();
            this.#cancelDrag();
            return;
        }
        if (this.#session) {
            if (event.key === "Escape" ||
                event.key === "Backspace" ||
                event.key === "Delete" ||
                event.key === "Enter") {
                event.preventDefault?.();
            }
            this.#applyDrawSnapshot(this.#session.keyDown(event.key));
            return;
        }
        if ((event.key === "Delete" || event.key === "Backspace") &&
            this.#selection.size > 0) {
            event.preventDefault?.();
            this.#callbacks.removeSelection?.();
            return;
        }
        if (event.key === "Escape" && this.#selection.size > 0) {
            event.preventDefault?.();
            this.select(undefined);
        }
    };
    constructor(map, registry, store, renderer, callbacks, options = {}, selection) {
        this.#map = map;
        this.#registry = registry;
        this.#store = store;
        this.#renderer = renderer;
        this.#callbacks = callbacks;
        this.#idFactory = options.idFactory ?? createDefaultIdFactory();
        this.#ownsSelection = selection === undefined;
        this.#selection = selection ?? new SelectionController(store);
        this.#map.on("click", this.#onClick);
        this.#map.on("dblclick", this.#onDoubleClick);
        this.#map.on("mousemove", this.#onMouseMove);
        this.#map.on("mousedown", this.#onMouseDown);
        this.#map.on("mouseup", this.#onMouseUp);
        this.#map.on("style.load", this.#onStyleLoad);
        const canvas = this.#map.getCanvas();
        if (canvas.tabIndex < 0)
            canvas.tabIndex = 0;
        canvas.addEventListener("keydown", this.#onKeyDown);
        this.#unsubscribeStore = this.#store.subscribe(() => {
            if (!this.#drag)
                this.#syncSelection();
        });
        this.#unsubscribeSelection = this.#selection.subscribe(() => {
            if (!this.#drag)
                this.#syncSelection();
        });
    }
    startDraw(plotType, options = {}) {
        const definition = this.#registry.get(plotType);
        const { minPoints, maxPoints, completeOnDoubleClick } = definition.controlSchema;
        if (minPoints < 2 || maxPoints < minPoints) {
            throw new Error(`Interactive drawing requires a valid point range; "${plotType}" declares ${minPoints}-${maxPoints}.`);
        }
        this.cancelDraw();
        this.#cancelDrag();
        this.select(undefined);
        this.#drawRejection = undefined;
        const id = options.id ?? this.#idFactory();
        const sessionOptions = {
            id,
            plotType,
            definitionVersion: definition.version,
            parameters: {
                ...definition.defaultParameters,
                ...(options.parameters ?? {}),
            },
            style: {
                ...definition.defaultStyle,
                ...(options.style ?? {}),
            },
            metadata: { ...(options.metadata ?? {}) },
            validateCompletion: (candidate) => this.#validateRenderableInput(candidate),
        };
        this.#session =
            minPoints === 2 && maxPoints === 2
                ? new TwoPointDrawSession(sessionOptions)
                : new MultiPointDrawSession({
                    ...sessionOptions,
                    minimumPoints: minPoints,
                    maximumPoints: maxPoints,
                    completeAtMaximum: completeOnDoubleClick !== true,
                    deriveDraftControlPoints: definition.deriveDraftControlPoints,
                });
        this.#disableDoubleClickZoom();
        this.#setCursor("crosshair");
        this.#focusCanvas();
        return id;
    }
    cancelDraw() {
        if (!this.#session)
            return false;
        this.#session.cancel();
        this.#session = undefined;
        this.#draftFeature = undefined;
        this.#drawRejection = undefined;
        this.#renderer.clearDraft();
        this.#restoreDoubleClickZoom();
        this.#setCursor("");
        return true;
    }
    select(id) {
        if (id === undefined) {
            this.#selection.clear();
        }
        else {
            this.#selection.replace([id]);
        }
        this.#syncSelection();
    }
    get selectedId() {
        return this.#selection.primaryId;
    }
    get selectedIds() {
        return this.#selection.selectedIds;
    }
    get selection() {
        return this.#selection;
    }
    get isDrawing() {
        return this.#session !== undefined;
    }
    get drawRejection() {
        return this.#drawRejection;
    }
    destroy() {
        this.cancelDraw();
        this.#cancelDrag();
        this.#restoreDoubleClickZoom();
        this.#unsubscribeSelection();
        this.#unsubscribeStore();
        if (this.#ownsSelection)
            this.#selection.destroy();
        this.#map.off("click", this.#onClick);
        this.#map.off("dblclick", this.#onDoubleClick);
        this.#map.off("mousemove", this.#onMouseMove);
        this.#map.off("mousedown", this.#onMouseDown);
        this.#map.off("mouseup", this.#onMouseUp);
        this.#map.off("style.load", this.#onStyleLoad);
        this.#map.getCanvas().removeEventListener("keydown", this.#onKeyDown);
        this.#clearClickSuppression();
        this.#renderer.clearSelection();
        this.#renderer.clearHandles();
    }
    #applyDrawSnapshot(snapshot, options = {}) {
        this.#drawRejection = snapshot.rejection;
        if (snapshot.draft) {
            try {
                const draft = this.#materialize(snapshot.draft);
                this.#draftFeature = draft;
                this.#renderer.renderDraft(draft, this.#registry);
            }
            catch {
                // Pointer candidates can briefly create coincident, self-intersecting,
                // or otherwise non-renderable geometry. Preserve the last valid full
                // draft when one exists. Before the first valid polygon, show the
                // semantic control path and points so drawing never appears blank.
                if (!this.#draftFeature) {
                    this.#renderer.renderDraftGuide(snapshot.draft);
                }
            }
        }
        else if (snapshot.status === "ready" || snapshot.status === "drawing") {
            this.#draftFeature = undefined;
            this.#renderer.clearDraft();
        }
        if (snapshot.completed) {
            const created = this.#callbacks.create(snapshot.completed);
            this.#session = undefined;
            this.#draftFeature = undefined;
            this.#drawRejection = undefined;
            this.#renderer.clearDraft();
            if (options.deferDoubleClickZoomRestore === true) {
                this.#scheduleDoubleClickZoomRestore();
            }
            else {
                this.#restoreDoubleClickZoom();
            }
            this.select(created.id);
            this.#setCursor("grab");
            return;
        }
        if (snapshot.status === "cancelled") {
            this.#session = undefined;
            this.#draftFeature = undefined;
            this.#drawRejection = undefined;
            this.#renderer.clearDraft();
            if (options.deferDoubleClickZoomRestore === true) {
                this.#scheduleDoubleClickZoomRestore();
            }
            else {
                this.#restoreDoubleClickZoom();
            }
            this.#setCursor("");
        }
    }
    #validateRenderableInput(input) {
        try {
            const feature = this.#createMaterializedFeature(input);
            const validation = this.#registry.validate(feature);
            if (!validation.valid)
                return validation;
            this.#registry.generate(feature);
            return validation;
        }
        catch (error) {
            return {
                valid: false,
                issues: [
                    {
                        code: "DRAW_CANDIDATE_GENERATION_FAILED",
                        message: error instanceof Error
                            ? error.message
                            : "Draw candidate generation failed.",
                        severity: "error",
                    },
                ],
            };
        }
    }
    #materialize(input) {
        const feature = this.#createMaterializedFeature(input);
        this.#registry.generate(feature);
        return feature;
    }
    #createMaterializedFeature(input) {
        const definition = this.#registry.get(input.plotType);
        return createPlotFeature({
            ...input,
            definitionVersion: input.definitionVersion ?? definition.version,
            parameters: {
                ...definition.defaultParameters,
                ...(input.parameters ?? {}),
            },
            style: {
                ...definition.defaultStyle,
                ...(input.style ?? {}),
            },
        });
    }
    #updateDrag(position) {
        const drag = this.#drag;
        if (!drag)
            return;
        const current = drag.preview.controlPoints[drag.handleIndex];
        if (current && samePosition(current, position))
            return;
        const controlPoints = drag.preview.controlPoints.map((point, index) => index === drag.handleIndex ? position : point);
        const preview = createPlotFeature({
            ...drag.original,
            controlPoints,
            revision: drag.original.revision + 1,
        });
        try {
            this.#registry.generate(preview);
        }
        catch {
            return;
        }
        drag.preview = preview;
        drag.moved = !sameControlPoints(drag.original.controlPoints, preview.controlPoints);
        this.#draftFeature = preview;
        this.#renderer.renderDraft(preview, this.#registry);
        this.#renderer.renderHandles(preview);
        this.#setCursor("grabbing");
    }
    #cancelDrag() {
        const drag = this.#drag;
        if (!drag)
            return false;
        this.#drag = undefined;
        this.#draftFeature = undefined;
        this.#renderer.clearDraft();
        this.#restoreDragPan(drag);
        this.#syncSelection();
        this.#setCursor(this.#selection.primaryId ? "grab" : "");
        return true;
    }
    #restoreDragPan(drag) {
        if (drag.dragPanWasEnabled)
            this.#map.dragPan?.enable();
    }
    #disableDoubleClickZoom() {
        this.#clearDoubleClickZoomRestoreTimer();
        if (this.#doubleClickZoomWasEnabled !== undefined)
            return;
        const zoom = this.#map.doubleClickZoom;
        if (!zoom)
            return;
        const wasEnabled = zoom.isEnabled?.() ?? true;
        this.#doubleClickZoomWasEnabled = wasEnabled;
        if (wasEnabled)
            zoom.disable();
    }
    #scheduleDoubleClickZoomRestore() {
        if (this.#doubleClickZoomWasEnabled === undefined)
            return;
        this.#clearDoubleClickZoomRestoreTimer();
        this.#doubleClickZoomRestoreTimer = setTimeout(() => {
            this.#doubleClickZoomRestoreTimer = undefined;
            if (!this.#session)
                this.#restoreDoubleClickZoom();
        }, 0);
    }
    #restoreDoubleClickZoom() {
        this.#clearDoubleClickZoomRestoreTimer();
        if (this.#doubleClickZoomWasEnabled === undefined)
            return;
        if (this.#doubleClickZoomWasEnabled) {
            this.#map.doubleClickZoom?.enable();
        }
        this.#doubleClickZoomWasEnabled = undefined;
    }
    #clearDoubleClickZoomRestoreTimer() {
        if (this.#doubleClickZoomRestoreTimer !== undefined) {
            clearTimeout(this.#doubleClickZoomRestoreTimer);
            this.#doubleClickZoomRestoreTimer = undefined;
        }
    }
    #syncSelection() {
        const selectedFeatures = this.#selection.selectedIds
            .map((id) => this.#store.find(id))
            .filter((feature) => feature !== undefined);
        const primaryId = this.#selection.primaryId;
        this.#renderer.renderSelection(selectedFeatures, primaryId, this.#registry);
        if (!primaryId) {
            this.#renderer.clearHandles();
            return;
        }
        const primary = selectedFeatures.find((feature) => feature.id === primaryId);
        if (!primary) {
            this.#renderer.clearHandles();
            return;
        }
        this.#renderer.renderHandles(primary);
    }
    #queryPlotId(event) {
        if (!event.point || !this.#map.queryRenderedFeatures)
            return undefined;
        const features = this.#map.queryRenderedFeatures(event.point, {
            layers: [
                this.#renderer.layerIds.selectionPoint,
                this.#renderer.layerIds.selectionLine,
                this.#renderer.layerIds.point,
                this.#renderer.layerIds.line,
                this.#renderer.layerIds.fill,
            ],
        });
        for (const feature of features) {
            const plotId = readString(feature.properties?.plotId);
            if (plotId !== undefined)
                return plotId;
        }
        return undefined;
    }
    #queryHandle(event) {
        if (!event.point || !this.#map.queryRenderedFeatures)
            return undefined;
        return this.#map.queryRenderedFeatures(event.point, {
            layers: [this.#renderer.layerIds.handle],
        })[0];
    }
    #armClickSuppression() {
        this.#clearClickSuppression();
        this.#suppressNextClick = true;
        this.#clickSuppressionTimer = setTimeout(() => {
            this.#suppressNextClick = false;
            this.#clickSuppressionTimer = undefined;
        }, 0);
    }
    #clearClickSuppression() {
        this.#suppressNextClick = false;
        if (this.#clickSuppressionTimer !== undefined) {
            clearTimeout(this.#clickSuppressionTimer);
            this.#clickSuppressionTimer = undefined;
        }
    }
    #setCursor(cursor) {
        this.#map.getCanvas().style.cursor = cursor;
    }
    #focusCanvas() {
        this.#map.getCanvas().focus?.();
    }
}
function asMouseEvent(event) {
    if (typeof event !== "object" || event === null)
        return undefined;
    const lngLat = event.lngLat;
    if (typeof lngLat !== "object" || lngLat === null)
        return undefined;
    const lng = lngLat.lng;
    const lat = lngLat.lat;
    if (typeof lng !== "number" || typeof lat !== "number")
        return undefined;
    return event;
}
function toPosition(event) {
    return [event.lngLat.lng, event.lngLat.lat];
}
function readSelectionIntent(event) {
    const original = event.originalEvent;
    if (original?.altKey === true)
        return "subtract";
    if (original?.ctrlKey === true || original?.metaKey === true)
        return "toggle";
    if (original?.shiftKey === true)
        return "add";
    return "replace";
}
function readString(value) {
    return typeof value === "string" ? value : undefined;
}
function readInteger(value) {
    return typeof value === "number" && Number.isInteger(value)
        ? value
        : undefined;
}
function samePosition(left, right) {
    return left[0] === right[0] && left[1] === right[1];
}
function sameControlPoints(left, right) {
    return (left.length === right.length &&
        left.every((point, index) => {
            const other = right[index];
            return other !== undefined && samePosition(point, other);
        }));
}
function createDefaultIdFactory() {
    let counter = 0;
    return () => {
        counter += 1;
        return `plot-${Date.now().toString(36)}-${counter.toString(36)}`;
    };
}
//# sourceMappingURL=interaction.js.map